import type { SubsystemSnapshot } from "../types";

import {
  OFFER_PLAN_MONTHS,
  PricingPortError,
  recommendedPlanMonths,
  validatePricingPayload,
  type OfferPlanMonths,
  type PricingFailure,
  type PricingMarket,
  type PricingPort,
  type ValidatedPricingPlan,
} from "./pricing";

export type OfferAction =
  | { readonly kind: "link"; readonly label: string; readonly href: string }
  | { readonly kind: "text"; readonly label: string };

export interface OfferPlanSnapshot {
  readonly months: OfferPlanMonths;
  readonly totalMinor: number | null;
  readonly total: string;
  readonly baseTotal: string | null;
  readonly monthly: string;
  readonly billing: string;
  readonly recommended: boolean;
  readonly action: OfferAction;
}

export interface PricingSnapshot {
  readonly status: "idle" | "loading" | "ready" | "error";
  readonly locale: string;
  readonly market: PricingMarket | null;
  readonly currency: string | null;
  readonly message: string;
  readonly error: PricingFailure | null;
  readonly plans: readonly OfferPlanSnapshot[];
}

export interface OfferSnapshot extends SubsystemSnapshot {
  readonly state: "managed";
  readonly pricing: PricingSnapshot;
  readonly trial: OfferAction;
  readonly install: {
    readonly windows: OfferAction;
    readonly extension: OfferAction;
    readonly macos: Extract<OfferAction, { kind: "text" }>;
  };
}

export interface OfferTargetConfig {
  readonly registration?: string | null;
  readonly windowsInstaller?: string | null;
  readonly chromeWebStore?: string | null;
  readonly checkout?: Partial<Record<OfferPlanMonths, string | null>>;
}

export interface OfferViewPort {
  render(snapshot: OfferSnapshot): void;
  destroy?(): void;
}

interface ValidatedTargets {
  registration: string | null;
  windowsInstaller: string | null;
  chromeWebStore: string | null;
  checkout: Partial<Record<OfferPlanMonths, string>>;
}

interface OfferCopy {
  loading: string;
  idle: string;
  ready: (market: PricingMarket, currency: string) => string;
  errors: Readonly<Record<PricingFailure, string>>;
  monthly: (value: string) => string;
  billing: (months: OfferPlanMonths) => string;
  waitingMonthly: string;
  choose: string;
  chooseWaiting: string;
  trial: string;
  registrationMissing: string;
  windows: string;
  extension: string;
  targetMissing: string;
  macosSoon: string;
}

const copyFor = (locale: string): OfferCopy => /^ru(?:-|_|$)/i.test(locale) ? {
  loading: "Загружаем актуальные цены…",
  idle: "Актуальные цены появятся после подключения к API.",
  ready: (market, currency) => `Цены обновлены · рынок ${market} · валюта ${currency}.`,
  errors: {
    aborted: "Загрузка цен прервана. Попробуйте ещё раз.",
    http: "Сервис цен временно недоступен. Попробуйте ещё раз позже.",
    malformed: "Сервис вернул неполные данные. Цены не показаны.",
    network: "Не удалось связаться с сервисом цен. Проверьте соединение и попробуйте ещё раз.",
    timeout: "Сервис цен не ответил вовремя. Попробуйте ещё раз.",
  },
  monthly: (value) => `${value} / месяц`,
  billing: (months) => months === 1 ? "Оплата за месяц" : `Оплата одним платежом за ${months} ${months === 3 ? "месяца" : "месяцев"}`,
  waitingMonthly: "Эквивалент в месяц появится после загрузки",
  choose: "Выбрать срок",
  chooseWaiting: "Выбор срока скоро",
  trial: "Начать 7 дней бесплатно",
  registrationMissing: "Ссылка регистрации готовится",
  windows: "Скачать для Windows",
  extension: "Установить Extension",
  targetMissing: "Ссылка готовится",
  macosSoon: "macOS — скоро",
} : {
  loading: "Loading current prices…",
  idle: "Current prices will appear after the API connects.",
  ready: (market, currency) => `Prices updated · market ${market} · currency ${currency}.`,
  errors: {
    aborted: "Price loading was interrupted. Try again.",
    http: "The pricing service is temporarily unavailable. Try again later.",
    malformed: "The service returned incomplete data. No prices are shown.",
    network: "Could not reach the pricing service. Check your connection and try again.",
    timeout: "The pricing service did not respond in time. Try again.",
  },
  monthly: (value) => `${value} / month`,
  billing: (months) => months === 1 ? "Billed monthly" : `Billed once for ${months} months`,
  waitingMonthly: "Monthly equivalent appears after loading",
  choose: "Choose term",
  chooseWaiting: "Plan selection coming soon",
  trial: "Start 7 days free",
  registrationMissing: "Registration link coming soon",
  windows: "Download for Windows",
  extension: "Install Extension",
  targetMissing: "Link coming soon",
  macosSoon: "macOS — coming soon",
};

const validatedHttps = (candidate: string | null | undefined): string | null => {
  if (!candidate) return null;
  try {
    const url = new URL(candidate);
    if (url.protocol !== "https:" || !url.hostname || url.username || url.password) return null;
    return url.href;
  } catch {
    return null;
  }
};

const validatedChromeWebStore = (candidate: string | null | undefined): string | null => {
  const href = validatedHttps(candidate);
  if (!href) return null;
  const url = new URL(href);
  if (url.hostname !== "chromewebstore.google.com" || url.port) return null;
  const segments = url.pathname.split("/").filter(Boolean);
  if ((segments.length !== 2 && segments.length !== 3) || segments[0] !== "detail") return null;
  const extensionId = segments.at(-1);
  if (!extensionId || !/^[a-p]{32}$/.test(extensionId)) return null;
  if (segments.length === 3 && !/^[a-z0-9][a-z0-9-]*$/.test(segments[1] ?? "")) return null;
  return href;
};

const copyAction = (action: OfferAction): OfferAction => ({ ...action });

export class OfferController {
  private locale = "ru-RU";
  private pricing: PricingSnapshot;
  private targets: ValidatedTargets = {
    registration: null,
    windowsInstaller: null,
    chromeWebStore: null,
    checkout: {},
  };
  private sequence = 0;
  private activeRequest: AbortController | null = null;
  private destroyed = false;

  constructor(private readonly port: PricingPort, private readonly view?: OfferViewPort) {
    this.pricing = this.emptyPricing("idle", this.locale, null);
    this.publish();
  }

  async load(locale: string, signal: AbortSignal): Promise<OfferSnapshot> {
    if (this.destroyed) throw new Error("OfferController has been destroyed.");
    const requestId = ++this.sequence;
    this.activeRequest?.abort(new DOMException("Pricing locale changed.", "AbortError"));
    const requestController = new AbortController();
    this.activeRequest = requestController;
    const forwardAbort = (): void => requestController.abort(signal.reason);
    if (signal.aborted) forwardAbort();
    else signal.addEventListener("abort", forwardAbort, { once: true });

    this.locale = locale;
    this.pricing = this.emptyPricing("loading", locale, null);
    this.publish();

    try {
      const payload = await this.port.load(locale, requestController.signal);
      if (requestId !== this.sequence || this.destroyed) return this.snapshot();
      if (requestController.signal.aborted) {
        this.pricing = this.emptyPricing("error", locale, "aborted");
      } else {
        const data = validatePricingPayload(payload);
        this.pricing = this.readyPricing(locale, data.market, data.currency, data.plans);
      }
    } catch (error) {
      if (requestId !== this.sequence || this.destroyed) return this.snapshot();
      const failure: PricingFailure = error instanceof PricingPortError ? error.kind : "malformed";
      this.pricing = this.emptyPricing("error", locale, failure);
    } finally {
      signal.removeEventListener("abort", forwardAbort);
      if (requestId === this.sequence) this.activeRequest = null;
    }

    if (requestId === this.sequence && !this.destroyed) this.publish();
    return this.snapshot();
  }

  setInstallTargets(config: OfferTargetConfig): void {
    const checkout: Partial<Record<OfferPlanMonths, string>> = {};
    for (const months of OFFER_PLAN_MONTHS) {
      const href = validatedHttps(config.checkout?.[months]);
      if (href) checkout[months] = href;
    }
    this.targets = {
      registration: validatedHttps(config.registration),
      windowsInstaller: validatedHttps(config.windowsInstaller),
      chromeWebStore: validatedChromeWebStore(config.chromeWebStore),
      checkout,
    };
    if (this.pricing.status === "ready") {
      this.pricing = { ...this.pricing, plans: this.pricing.plans.map((plan) => ({
        ...plan,
        action: this.planAction(plan.months, this.locale),
      })) };
    }
    this.publish();
  }

  snapshot(): OfferSnapshot {
    const copy = copyFor(this.locale);
    return {
      state: "managed",
      pricing: {
        ...this.pricing,
        plans: this.pricing.plans.map((plan) => ({ ...plan, action: copyAction(plan.action) })),
      },
      trial: this.targets.registration
        ? { kind: "link", label: copy.trial, href: this.targets.registration }
        : { kind: "text", label: copy.registrationMissing },
      install: {
        windows: this.targets.windowsInstaller
          ? { kind: "link", label: copy.windows, href: this.targets.windowsInstaller }
          : { kind: "text", label: copy.targetMissing },
        extension: this.targets.chromeWebStore
          ? { kind: "link", label: copy.extension, href: this.targets.chromeWebStore }
          : { kind: "text", label: copy.targetMissing },
        macos: { kind: "text", label: copy.macosSoon },
      },
    };
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.sequence += 1;
    this.activeRequest?.abort(new DOMException("OfferController destroyed.", "AbortError"));
    this.activeRequest = null;
    this.view?.destroy?.();
  }

  private emptyPricing(
    status: "idle" | "loading" | "error",
    locale: string,
    error: PricingFailure | null,
  ): PricingSnapshot {
    const copy = copyFor(locale);
    const message = status === "loading" ? copy.loading : error ? copy.errors[error] : copy.idle;
    return {
      status,
      locale,
      market: null,
      currency: null,
      message,
      error,
      plans: OFFER_PLAN_MONTHS.map((months) => ({
        months,
        totalMinor: null,
        total: "—",
        baseTotal: null,
        monthly: copy.waitingMonthly,
        billing: copy.billing(months),
        recommended: false,
        action: { kind: "text", label: copy.chooseWaiting },
      })),
    };
  }

  private readyPricing(
    locale: string,
    market: PricingMarket,
    currency: string,
    plans: readonly ValidatedPricingPlan[],
  ): PricingSnapshot {
    const copy = copyFor(locale);
    const formatter = new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
    const recommended = recommendedPlanMonths(plans);
    return {
      status: "ready",
      locale,
      market,
      currency,
      message: copy.ready(market, currency),
      error: null,
      plans: plans.map((plan) => ({
        months: plan.months,
        totalMinor: plan.effectiveAmountMinor,
        total: formatter.format(plan.effectiveAmountMinor / 100),
        baseTotal: plan.welcomeApplied && plan.baseAmountMinor > plan.effectiveAmountMinor
          ? formatter.format(plan.baseAmountMinor / 100)
          : null,
        monthly: copy.monthly(formatter.format(plan.effectiveAmountMinor / plan.months / 100)),
        billing: copy.billing(plan.months),
        recommended: recommended === plan.months,
        action: this.planAction(plan.months, locale),
      })),
    };
  }

  private planAction(months: OfferPlanMonths, locale: string): OfferAction {
    const copy = copyFor(locale);
    const href = this.targets.checkout[months];
    return href
      ? { kind: "link", label: copy.choose, href }
      : { kind: "text", label: copy.chooseWaiting };
  }

  private publish(): void {
    if (!this.destroyed) this.view?.render(this.snapshot());
  }
}
